import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight, faShieldHalved, faCertificate, faUserDoctor,
  faLocationDot, faPhone, faEnvelope, faClock, faStar,
  faChevronDown, faChevronLeft, faChevronRight, faCheckCircle, faPaperPlane, faGlobe, faPenNib,
  faUserSecret, faVideo, faLock, faLanguage, faComments
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';
// One shared list so the homepage can never drift from the database / admin tool.
import { LANGUAGES } from '../constants/Languages';

// import.meta.env does not exist under Create React App — VITE_BACKEND_URL was
// never populated, and depending on the webpack version the expression could
// fail to compile outright. Bare origin, used for /uploads/<image>.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

/* ─────────────────────────────────────────
   FALLBACK DATA
   Shown only when the API returns nothing. See the note in the component —
   these are placeholder people, not real practitioners.
───────────────────────────────────────── */
const ALL_DOCTORS = [
  { name: 'Dr. Priya Sharma', spec: 'Depression & Anxiety', filter: 'Depression & Anxiety', rating: 4.9, bio: 'I work with people who are exhausted from holding it together. You don\'t need a crisis to talk to me — just a feeling that something isn\'t right.', experience: '8 yrs', languages: ['Assamese', 'Hindi', 'English'], sessions: '240+' },
  { name: 'Dr. Arjun Bora', spec: 'Trauma & PTSD', filter: 'Trauma & PTSD', rating: 4.8, bio: 'Trauma doesn\'t define you. I help people reclaim their story — at whatever pace feels right. No rushing, no judgement.', experience: '11 yrs', languages: ['Assamese', 'Bengali', 'English'], sessions: '310+' },
  { name: 'Dr. Meena Kalita', spec: 'Child Psychology', filter: 'Child Psychology', rating: 5.0, bio: 'Children don\'t always have words for what they feel. I help them find those words — through play, stories, and a lot of patience.', experience: '9 yrs', languages: ['Assamese', 'Hindi', 'English'], sessions: '180+' },
  { name: 'Dr. Rohan Das', spec: 'Addiction Recovery', filter: 'Addiction Recovery', rating: 4.7, bio: 'Recovery isn\'t about willpower. It\'s about understanding what the habit is trying to solve. Let\'s figure that out together.', experience: '7 yrs', languages: ['Bengali', 'Hindi', 'English'], sessions: '200+' },
  { name: 'Millenty Lhoujem', spec: 'Child Psychology', filter: 'Child Psychology', bio: 'Pediatric Behavior Therapist specialising in neurodivergent children. I believe every child has their own way of making sense of the world.', experience: '2 yrs', languages: ['Mizo', 'English', 'Hindi'], sessions: '60+', image: 'Millen.png' },
  { name: 'Meghna Lahkar', spec: 'Adult Psycho-therapy', filter: ['Child Psychology', 'Rehabilition psychology', 'Adult Psycho-therapy'], bio: 'Whether it\'s school stress, addiction, or just life feeling too heavy — I\'ve sat with all of it. You can bring whatever you\'re carrying.', experience: '4 yrs', languages: ['Assamese', 'Bengali', 'English'], sessions: '120+', image: 'Meghna.png' },
];

const FILTER_PILLS = [
  'All',
  'Anxiety',
  'Depression',
  'Stress & Burnout',
  'Trauma & PTSD',
  'Relationships',
  'Family & Parenting',
  'Career & Life Transitions',
  'Academic & Exam Pressure',
  'Addiction & Recovery',
  'Child & Adolescent',
  'Neurodevelopmental',
  'Behavioural Therapy',
  'Self-Esteem & Life Adjustment',
  'Rehabilitation & Disability',
];

const ROTATING_PHRASES = [
  'this long enough.',
  'it all on your own.',
  'in silence.',
  'what stays hidden.',
];

// `area` must be one of FILTER_PILLS — it is what the checklist hands to the
// directory (?focus=...) so ticking a card actually leads somewhere. A null
// area means "not sure"; those cards point to the matching quiz instead.
const REAL_PROBLEMS = [
  { emoji: '😶', text: 'I smile at work but feel empty at home.', area: 'Depression' },
  { emoji: '🌙', text: 'I lie awake at 2am overthinking everything.', area: 'Anxiety' },
  { emoji: '📱', text: 'I\'m surrounded by people but feel completely alone.', area: 'Depression' },
  { emoji: '😤', text: 'I get angry easily and I don\'t know why.', area: 'Stress & Burnout' },
  { emoji: '📚', text: 'Exam pressure feels like it\'s crushing me.', area: 'Academic & Exam Pressure' },
  { emoji: '👴', text: 'Nobody asks how I\'m doing anymore.', area: 'Self-Esteem & Life Adjustment' },
  { emoji: '🔄', text: 'I keep repeating the same patterns in relationships.', area: 'Relationships' },
  { emoji: '🤷', text: 'I don\'t even know what\'s wrong — I just know something is.', area: null },
];

const TESTIMONIALS = [
  { quote: 'I didn\'t think I needed help. I just thought everyone felt this way. Turns out they don\'t.', who: '23 years old, Guwahati', tag: 'Anxiety' },
  { quote: 'My parents didn\'t understand why I was struggling in college. My therapist helped me explain it to them.', who: '19 years old, Dibrugarh', tag: 'Family pressure' },
  { quote: 'The first session I cried for the full hour. By the third, I laughed. That surprised me.', who: '34 years old, Jorhat', tag: 'Depression' },
  { quote: 'Being able to talk in Assamese made everything easier. I could say exactly what I meant.', who: '41 years old, Assam', tag: 'Language comfort' },
];

const FAQS = [
  { q: 'Is everything I share really confidential?', a: 'Yes — completely. Every session is end-to-end encrypted. Your therapist is bound by clinical confidentiality, and we never share data with anyone. What you say stays between you and your therapist.' },
  { q: 'Can I talk to a therapist in Assamese or my regional language?', a: 'Absolutely — this is one of the things we built UXAAH for. Our therapists speak Assamese, Bengali, Bodo, Mizo, Hindi, Nepali, Manipuri, Mundari, Sadri and English. You deserve to express yourself in the language that feels most natural to you.' },
  { q: 'Do I need to be really struggling to book a session?', a: 'No. You don\'t need a diagnosis or a crisis. Many people come because they\'re tired, or something feels off, or they just want space to think out loud. You don\'t need to justify needing help.' },
  { q: 'What if I don\'t connect with my therapist?', a: 'That\'s completely okay — and more common than you think. You can switch therapists at any time, no questions asked. Finding the right fit is part of the process and we\'ll help you get there.' },
  { q: 'How much does it cost?', a: 'We have flexible plans that make therapy accessible. You\'ll see transparent pricing before you book — no hidden fees, ever. We also offer sliding scale options for students and those in financial difficulty.' },
  { q: 'My parents / family don\'t believe in therapy. What do I do?', a: 'You\'re not alone in this. A huge part of what we do at UXAAH is normalising mental health care in Northeast India. Sessions are private. You don\'t need anyone\'s permission to take care of yourself.' },
];

const STATS = [
  { num: '1 in 7', label: 'Indians lives with a mental health condition' },
  { num: '80%', label: 'never speak to anyone about it' },
  { num: '8+', label: 'languages spoken by our therapists' },
  { num: '100%', label: 'of our therapists are licensed & verified' },
];

/* ─────────────────────────────────────────
   BREATHING
───────────────────────────────────────── */
const BREATH_PHASES = [
  { label: 'Breathe in', seconds: 4 },
  { label: 'Hold', seconds: 7 },
  { label: 'Breathe out', seconds: 8 },
];

const useBreathing = () => {
  const [active, setActive] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const intervalRef = useRef(null);
  const phaseTimerRef = useRef(null);

  const clearAll = useCallback(() => {
    clearInterval(intervalRef.current);
    clearTimeout(phaseTimerRef.current);
  }, []);

  const startPhase = useCallback((idx) => {
    const phase = BREATH_PHASES[idx];
    setPhaseIdx(idx);
    setCountdown(phase.seconds);
    let remaining = phase.seconds;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => { remaining -= 1; setCountdown(remaining); }, 1000);
    clearTimeout(phaseTimerRef.current);
    phaseTimerRef.current = setTimeout(() => startPhase((idx + 1) % BREATH_PHASES.length), phase.seconds * 1000);
  }, []);

  const toggle = useCallback(() => {
    if (active) { clearAll(); setActive(false); setCountdown(null); setPhaseIdx(0); }
    else { setActive(true); startPhase(0); }
  }, [active, clearAll, startPhase]);

  useEffect(() => () => clearAll(), [clearAll]);
  return { active, phaseIdx, countdown, toggle };
};

/* ─────────────────────────────────────────
   STAR RATING
───────────────────────────────────────── */
const StarRating = ({ rating }) => {
  if (!rating) return null;
  return (
    <div className="doc-stars">
      <FontAwesomeIcon icon={faStar} className="star-filled" />
      <span className="doc-rating-num">{rating}</span>
    </div>
  );
};

/* ─────────────────────────────────────────
   CARD RAIL
   Horizontal scroller for the therapist and graphologist rows. 14 cards in a
   grid pushed everything below them — testimonials, breathing, FAQ — a very
   long way down the page.
───────────────────────────────────────── */
const CardRail = ({ children, label }) => {
  const railRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateArrows = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, children]);

  const scrollBy = (direction) => {
    const el = railRef.current;
    if (!el) return;
    // One card plus its gap, so a click lands cleanly on the next card.
    const card = el.querySelector('.pro');
    const step = card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: step * direction, behavior: 'smooth' });
  };

  return (
    <div className="rail-outer">
      <button
        type="button"
        className="rail-arrow rail-arrow--prev"
        onClick={() => scrollBy(-1)}
        disabled={atStart}
        aria-label={`Scroll ${label} left`}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>

      <div className="rail" ref={railRef} tabIndex={0} aria-label={label}>
        {children}
      </div>

      <button
        type="button"
        className="rail-arrow rail-arrow--next"
        onClick={() => scrollBy(1)}
        disabled={atEnd}
        aria-label={`Scroll ${label} right`}
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const HomePage = () => {
  const navigate = useNavigate();
  const observerRef = useRef(null);
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');
  const [checkedProblems, setCheckedProblems] = useState([]);
  const [showProblemsResult, setShowProblemsResult] = useState(false);

  const [quickIssue, setQuickIssue] = useState('');

  const [dbTherapists, setDbTherapists] = useState([]);
  const [dbGraphologists, setDbGraphologists] = useState([]);

  const { active: breathing, phaseIdx: breathPhaseIdx, countdown, toggle: toggleBreathing } = useBreathing();

  useEffect(() => {
    axios.get('/doctors')
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        const safeData = list.map(doc => {
          // Backend now returns `specialisations` as an array (a therapist can
          // have more than one, per migration-specialisations.sql). The old
          // single `doc.specialisation` string never existed in practice —
          // this is what actually fixes "General Therapy" showing on every
          // card once an admin has filled specialisations in.
          const specList = Array.isArray(doc.specialisations) ? doc.specialisations : [];
          return {
            id: doc.id,
            name: doc.name,
            spec: specList.length > 0 ? specList : 'General Therapy',
            filter: specList.length > 0 ? specList[0] : 'All',
            bio: doc.bio || doc.description || 'Passionate about creating a safe space for you.',
            experience: doc.experience || '',
            languages: Array.isArray(doc.languages) ? doc.languages : [],
            sessions: doc.sessions || '',
            image: doc.image
          };
        });
        setDbTherapists(safeData);
      })
      .catch(() => {});

    // Graphologists now come from the database rather than a hardcoded list.
    axios.get('/graphologists')
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setDbGraphologists(list.map(g => ({
          id: g.id,
          name: g.name,
          spec: g.specialisation || 'Graphologist',
          bio: g.bio || 'Handwriting analysis for self-understanding.',
          experience: g.experience || '',
          sessions: g.sessions || '',
          image: g.image
        })));
      })
      .catch(() => {});
  }, []);

  const displayDoctors = dbTherapists.length > 0 ? dbTherapists : ALL_DOCTORS;

  // The hero dropdown is built from specialisations real therapists have, so
  // you can never pick an area that leads to an empty list. Until the API
  // answers (or if it fails) it falls back to the full standard list.
  const issueCounts = {};
  dbTherapists.forEach((d) => {
    (Array.isArray(d.spec) ? d.spec : []).forEach((s) => {
      issueCounts[s] = (issueCounts[s] || 0) + 1;
    });
  });
  const haveIssueData = Object.keys(issueCounts).length > 0;
  const issueOptions = haveIssueData
    ? Object.keys(issueCounts).sort((a, b) => issueCounts[b] - issueCounts[a] || a.localeCompare(b))
    : FILTER_PILLS.filter((p) => p !== 'All');


  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('visible'); observer.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    observerRef.current = observer;
    document.querySelectorAll('.animate-on-scroll:not(.visible)').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [dbTherapists, dbGraphologists]);

  useEffect(() => {
    const id = setInterval(() => setPhraseIdx(i => (i + 1) % ROTATING_PHRASES.length), 3200);
    return () => clearInterval(id);
  }, []);

  const toggleProblem = (i) => {
    setCheckedProblems(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
    setShowProblemsResult(false);
  };

  const handleProblemsSubmit = () => {
    if (checkedProblems.length > 0) setShowProblemsResult(true);
  };

  // The checklist is no longer just a mood board: whatever is ticked is
  // tallied by area and the top ones become links into the therapist
  // directory, filtered to that specialisation.
  const problemAreas = (() => {
    const counts = {};
    checkedProblems.forEach(i => {
      const area = REAL_PROBLEMS[i]?.area;
      if (area) counts[area] = (counts[area] || 0) + 1;
    });
    return Object.entries(counts)
      .filter(([area]) => !haveIssueData || issueCounts[area]) // only areas that have therapists
      .sort((a, b) => b[1] - a[1]).slice(0, 3).map(([area]) => area);
  })();

  const goToArea = (area) => navigate(`/doctors?tab=therapists&focus=${encodeURIComponent(area)}`);

  const scrollToTherapists = (e) => {
    if (e) e.preventDefault();
    const el = document.getElementById('doc1');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Sends the chosen area to the directory rather than filtering in place —
  // /doctors is where browsing and filtering belong.
  const handleQuickMatch = (e) => {
    e.preventDefault();
    if (quickIssue) {
      navigate(`/doctors?tab=therapists&focus=${encodeURIComponent(quickIssue)}`);
    } else {
      scrollToTherapists();
    }
  };

  const handleNewsletter = (e) => {
    e.preventDefault();
    if (!newsletterEmail.includes('@')) { setNewsletterStatus('Please enter a valid email'); return; }
    setNewsletterStatus('You\'re in 💌 — gentle tips on the way.');
    setNewsletterEmail('');
    setTimeout(() => setNewsletterStatus(''), 5000);
  };

  const currentPhase = BREATH_PHASES[breathPhaseIdx];

  return (
    <>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&display=swap');

      /* ── shared tokens ── */
      :root {
        --primary-color:  #303c6c;
        --primary-dark:   #1a2344;
        --primary-light:  #4a579d;
        --accent-orange:  #e8622a;
        --accent-warm:    #b48c5a;
        --accent-sage:    #4a7c6f;
        --sage-lite:      #e8f2ef;
        --clay:           #c85c2d;
        --clay-lite:      #f4e8df;
        --cream:          #faf6f0;
        --bg-light:       #f9fafc;
        --bg-lighter:     #ffffff;
        --text-primary:   #111827;
        --text-secondary: #374151;
        --text-light:     #6b7280;
        --text-muted:     #9ca3af;
        --border:         rgba(48,60,108,0.10);
        --shadow-sm:  0 4px 12px rgba(48,60,108,0.04),0 1px 2px rgba(0,0,0,0.02);
        --shadow-md:  0 12px 32px rgba(48,60,108,0.08),0 4px 12px rgba(0,0,0,0.04);
        --shadow-lg:  0 24px 60px rgba(48,60,108,0.12),0 8px 24px rgba(0,0,0,0.06);
        --navbar-h:   80px;
      }

      * { box-sizing: border-box; }
      body { margin: 0; font-family: 'DM Sans', sans-serif; background-color: var(--bg-lighter); color: var(--text-primary); }

      /* ── entrance animations ── */
      .animate-on-scroll { opacity:0; transform:translateY(24px); transition:opacity 0.7s ease,transform 0.7s ease; }
      .animate-on-scroll.visible { opacity:1; transform:none; }

      /* ════════════════════════════════════
         HERO
      ════════════════════════════════════ */
      #avatar {
        position: relative;
        width: 100%;
        min-height: calc(100vh - var(--navbar-h));
        overflow: hidden;
        display: flex;
        align-items: center;
        background: var(--bg-light);
      }
      .hero-bg-img {
        position: absolute;
        inset: 0;
        width: 100%;
        background-image: url('/img/hero.png');
        background-size: cover; 
        background-position: center; 
        background-repeat: no-repeat;
        z-index: 0;
      }
      .hero-container {
        position: relative; z-index: 2;
        display: flex; justify-content: space-between; align-items: center;
        width: 100%; max-width: 1400px; margin: 0 auto; padding: 40px 80px; gap: 40px;
      }
      .hero-copy { flex: 1; max-width: 600px; text-align: left; }
      
      .brand-badge {
        display: inline-block; background: var(--primary-dark); color: white;
        font-family: 'DM Serif Display', serif; font-size: 16px; padding: 6px 16px;
        border-radius: 8px; margin-bottom: 24px; letter-spacing: 0.05em;
        box-shadow: var(--shadow-sm); animation: heroFadeUp 0.5s ease both 0.05s;
      }
      .hero-eyebrow {
        display: inline-flex; align-items: center; gap: 8px;
        color: var(--primary-color); font-size: 13px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
        margin-bottom: 12px; animation: heroFadeUp 0.5s ease both 0.1s;
      }
      .hero-headline {
        font-family: 'DM Serif Display', Georgia, serif; font-size: clamp(42px, 5vw, 68px);
        line-height: 1.1; color: var(--primary-dark); letter-spacing: -0.02em; margin: 0 0 20px;
        animation: heroFadeUp 0.6s ease both 0.2s;
      }
      .hero-headline-accent { color: var(--accent-orange); font-style: italic; display: inline-block; min-height: 1.3em; animation: fadeSlideIn 600ms ease; }
      @keyframes fadeSlideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      
      .hero-subtext {
        font-size: 17px; line-height: 1.7; color: var(--text-secondary); margin: 0 0 32px;
        animation: heroFadeUp 0.6s ease both 0.4s;
      }
      
      .hero-trust-strip { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; margin-top: 24px; animation: heroFadeUp 0.5s ease both 0.5s; }
      .hero-trust-item { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: var(--primary-dark); }
      .hero-trust-icon { color: var(--accent-orange); font-size: 16px; }

      .hero-interactive-card {
        flex: 0 0 420px;
        background: rgba(255, 255, 255, 0.85);
        backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
        border: 1px solid rgba(255, 255, 255, 0.5); border-radius: 24px;
        padding: 40px; box-shadow: var(--shadow-lg);
        animation: heroFadeUp 0.7s cubic-bezier(0.2, 0.8, 0.2, 1) both 0.4s;
        margin-top: 180px; 
      }
      .interactive-title { font-family: 'DM Serif Display', serif; font-size: 24px; color: var(--primary-dark); margin: 0 0 8px; }
      .interactive-subtitle { font-size: 14px; color: var(--text-light); margin: 0 0 24px; }
      
      .form-group { margin-bottom: 20px; text-align: left; }
      .form-group label { display: block; font-size: 13px; font-weight: 600; color: var(--text-secondary); margin-bottom: 8px; }
      .form-select {
        width: 100%; padding: 14px 16px; border-radius: 12px;
        border: 1.5px solid rgba(48,60,108,0.15); font-family: 'DM Sans', sans-serif;
        font-size: 15px; color: var(--primary-dark); background: #fff;
        outline: none; transition: border-color 0.3s, box-shadow 0.3s;
        cursor: pointer;
      }
      .form-select:focus { border-color: var(--primary-color); box-shadow: 0 0 0 4px rgba(48,60,108,0.1); }
      
      .btn-primary-large {
        display: flex; align-items: center; justify-content: center; gap: 10px;
        width: 100%; padding: 16px; font-size: 16px; font-weight: 600;
        color: #fff; background: var(--primary-dark);
        border: none; border-radius: 12px; cursor: pointer; text-decoration: none;
        transition: all 0.3s cubic-bezier(0.4,0,0.2,1); box-shadow: 0 8px 24px rgba(26,35,68,0.25);
      }
      .btn-primary-large:hover { background: var(--primary-color); transform: translateY(-3px); box-shadow: 0 12px 32px rgba(26,35,68,0.35); }

      @keyframes heroFadeUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }

      @media (max-width: 1024px) {
        .hero-container { flex-direction: column; text-align: center; justify-content: center; padding: 60px 40px; }
        .hero-copy { max-width: 100%; display: flex; flex-direction: column; align-items: center; }
        .hero-trust-strip { justify-content: center; }
        .hero-interactive-card { width: 100%; max-width: 500px; flex: auto; margin-top: 40px; }
      }
      @media (max-width: 600px) {
        .hero-container { padding: 40px 20px; }
        .hero-headline { font-size: 36px; }
        .hero-interactive-card { padding: 32px 24px; margin-top: 30px; }
      }

      /* ════════════════════════════════════
         LANGUAGE STRIP
      ════════════════════════════════════ */
      .lang-strip {
        background: var(--primary-dark);
        padding: 18px 0; overflow: hidden; position: relative;
      }
      .lang-strip::before, .lang-strip::after { content:''; position:absolute; top:0; bottom:0; width:80px; z-index:2; }
      .lang-strip::before { left:0; background:linear-gradient(to right,var(--primary-dark),transparent); }
      .lang-strip::after  { right:0; background:linear-gradient(to left,var(--primary-dark),transparent); }
      .lang-label-row { text-align:center; padding:0 0 8px; font-size:11px; font-weight:700; letter-spacing:0.15em; text-transform:uppercase; color:rgba(255,255,255,0.4); margin:0; }
      .lang-scroll { display:flex; white-space:nowrap; animation:langScroll 22s linear infinite; }
      @keyframes langScroll { from{transform:translateX(0)} to{transform:translateX(-50%)} }
      .lang-item { display:inline-flex; align-items:center; gap:10px; padding:0 28px; font-size:14px; font-weight:500; color:rgba(255,255,255,0.6); }
      .lang-item strong { color:rgba(255,255,255,1); font-weight:600; }
      .lang-dot { width:6px; height:6px; border-radius:50%; background:var(--accent-orange); flex-shrink:0; }

      .eyebrow {
        font-size:12px; font-weight:700; letter-spacing:0.15em;
        text-transform:uppercase; color:var(--primary-light);
        margin-bottom:12px; display:block;
      }

      /* ════════════════════════════════════
         "DOES THIS SOUND LIKE YOU?" 
      ════════════════════════════════════ */
      .problems-section { background: var(--bg-light); padding: 100px 80px; position: relative; }
      .problems-section::after { 
        content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 100px;
        background: linear-gradient(to bottom, transparent, var(--bg-lighter)); pointer-events: none;
      }
      .problems-header { text-align:center; margin-bottom:48px; }
      .problems-section h2 { font-family:'DM Serif Display',Georgia,serif; font-size:clamp(32px,4vw,46px); color:var(--primary-dark); letter-spacing:-0.02em; margin:8px 0 16px; }
      .problems-section h2 em { font-style:italic; color:var(--accent-orange); }
      .problems-section .sub { font-size:17px; color:var(--text-light); line-height:1.7; max-width:600px; margin:0 auto; }
      
      .problems-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(240px, 1fr)); gap:16px; max-width:1100px; margin:0 auto 40px; position: relative; z-index: 2; }
      .problem-card {
        background:white; border:1px solid rgba(48,60,108,0.06); border-radius:20px; padding:24px 20px; 
        cursor:pointer; transition:all 0.3s cubic-bezier(0.4,0,0.2,1); display:flex; align-items:flex-start; gap:16px;
        box-shadow: var(--shadow-sm);
      }
      .problem-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); border-color: rgba(232,98,42,0.3); }
      .problem-card.checked { background:rgba(232,98,42,0.04); border-color:var(--accent-orange); box-shadow: 0 8px 24px rgba(232,98,42,0.12); transform: translateY(-4px); }
      
      .problem-check {
        width:22px; height:22px; border-radius:6px; border:1.5px solid rgba(48,60,108,0.2);
        flex-shrink:0; margin-top:2px; display:flex; align-items:center; justify-content:center;
        transition:all 0.2s; font-size:12px; color:white; background:white;
      }
      .problem-card.checked .problem-check { background:var(--accent-orange); border-color:var(--accent-orange); }
      .problem-emoji { font-size:24px; flex-shrink:0; }
      .problem-text { font-size:15px; font-weight:500; color:var(--text-secondary); line-height:1.5; margin:0; }
      
      .problems-cta-wrap { text-align:center; position: relative; z-index: 2; }
      .problems-result { margin:24px auto 0; max-width:620px; background:white; border:1px solid rgba(232,98,42,0.2); border-radius:20px; padding:32px; box-shadow:var(--shadow-md); animation: heroFadeUp 0.4s ease; }
      .problems-result p { font-size:16px; color:var(--text-secondary); line-height:1.7; margin:0 0 20px; }
      .problems-result strong { color:var(--primary-dark); font-size: 18px; display: block; margin-bottom: 8px; }
      
      @media (max-width:768px) { .problems-section { padding:80px 24px; } }

      .result-lead { font-weight:600; color:var(--primary-dark); margin-bottom:12px !important; }
      .result-areas { display:flex; flex-wrap:wrap; gap:10px; justify-content:center; margin-bottom:20px; }
      .result-area-btn {
        display:inline-flex; align-items:center; gap:8px; padding:12px 22px; border-radius:50px;
        border:2px solid var(--primary-dark); background:#fff; color:var(--primary-dark);
        font-family:'DM Sans',sans-serif; font-size:14px; font-weight:600; cursor:pointer; transition:all 0.25s ease;
      }
      .result-area-btn:hover { background:var(--primary-dark); color:#fff; transform:translateY(-2px); }
      .result-quiz-link { display:inline-block; font-size:14px; font-weight:600; color:var(--accent-orange); text-decoration:none; margin-top:4px; }
      .result-quiz-link:hover { text-decoration:underline; }
      .result-crisis { font-size:13px !important; color:var(--text-light) !important; margin:20px 0 0 !important; padding-top:16px; border-top:1px solid rgba(48,60,108,0.08); }

      /* hero quiz link */
      .hero-quiz-link {
        display:flex; align-items:center; justify-content:center; gap:8px; margin-top:14px; padding:12px 16px;
        border:1.5px dashed rgba(232,98,42,0.55); border-radius:12px; background:rgba(232,98,42,0.05);
        font-size:14px; font-weight:600; color:var(--accent-orange); text-decoration:none; transition:all 0.25s ease;
      }
      .hero-quiz-link:hover { background:rgba(232,98,42,0.12); border-style:solid; }
      .hero-quiz-note { text-align:center; font-size:12px; color:var(--text-light); margin:8px 0 0; }

      /* ════════════════════════════════════
         LANGUAGE BARRIER SECTION
      ════════════════════════════════════ */
      .langsec { background:var(--cream); padding:100px 80px; }
      .langsec-inner { max-width:1200px; margin:0 auto; display:grid; grid-template-columns:1.1fr 0.9fr; gap:72px; align-items:center; }
      .langsec h2 { font-family:'DM Serif Display',Georgia,serif; font-size:clamp(32px,4vw,46px); color:var(--primary-dark); letter-spacing:-0.02em; margin:8px 0 20px; line-height:1.15; }
      .langsec h2 em { font-style:italic; color:var(--accent-orange); }
      .langsec-lead { font-size:17px; line-height:1.8; color:var(--text-secondary); margin:0 0 28px; max-width:540px; }
      .langsec-points { list-style:none; margin:0 0 32px; padding:0; display:flex; flex-direction:column; gap:18px; }
      .langsec-points li { display:flex; gap:16px; align-items:flex-start; }
      .langsec-point-icon { width:40px; height:40px; border-radius:12px; background:var(--sage-lite); color:var(--accent-sage); display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
      .langsec-points strong { display:block; color:var(--primary-dark); font-size:16px; margin-bottom:2px; }
      .langsec-points span { font-size:15px; color:var(--text-secondary); line-height:1.6; }
      .langsec-card { background:#fff; border-radius:28px; padding:40px; box-shadow:var(--shadow-lg); border:1px solid rgba(48,60,108,0.06); }
      .langsec-card-title { font-family:'DM Serif Display',Georgia,serif; font-size:24px; color:var(--primary-dark); margin:0 0 20px; }
      .langsec-pills { display:flex; flex-wrap:wrap; gap:10px; }
      .langsec-pill { padding:10px 20px; border-radius:50px; background:var(--sage-lite); color:var(--accent-sage); font-size:15px; font-weight:600; }
      .langsec-note { font-size:14px; color:var(--text-light); line-height:1.6; margin:24px 0 0; padding-top:20px; border-top:1px solid rgba(48,60,108,0.08); }
      @media (max-width:900px) { .langsec { padding:80px 24px; } .langsec-inner { grid-template-columns:1fr; gap:40px; } .langsec-card { padding:28px; } }

      /* ════════════════════════════════════
         ANONYMOUS SECTION
      ════════════════════════════════════ */
      .anonsec { background:var(--primary-dark); padding:100px 80px; color:#fff; }
      .anonsec-inner { max-width:1100px; margin:0 auto; }
      .anonsec-head { text-align:center; max-width:680px; margin:0 auto 56px; }
      .anonsec .eyebrow { color:#f4a070; }
      .anonsec h2 { font-family:'DM Serif Display',Georgia,serif; font-size:clamp(32px,4vw,46px); letter-spacing:-0.02em; margin:8px 0 16px; line-height:1.15; color:#fff; }
      .anonsec h2 em { font-style:italic; color:#f4a070; }
      .anonsec-head p { font-size:17px; line-height:1.8; color:rgba(255,255,255,0.75); margin:0; }
      .anonsec-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:24px; }
      .anonsec-card { background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.1); border-radius:24px; padding:32px; }
      .anonsec-card-icon { width:52px; height:52px; border-radius:14px; background:rgba(232,98,42,0.18); color:#f4a070; display:flex; align-items:center; justify-content:center; font-size:20px; margin-bottom:20px; }
      .anonsec-card h3 { font-family:'DM Serif Display',Georgia,serif; font-size:22px; margin:0 0 10px; font-weight:400; color:#fff; }
      .anonsec-card p { font-size:15px; line-height:1.7; color:rgba(255,255,255,0.72); margin:0; }
      .anonsec-foot { text-align:center; margin-top:40px; font-size:14px; color:rgba(255,255,255,0.55); }
      @media (max-width:900px) { .anonsec { padding:80px 24px; } .anonsec-grid { grid-template-columns:1fr; } }

      /* ════════════════════════════════════
         STATS STRIP
      ════════════════════════════════════ */
      .stats-strip {
        background: var(--bg-lighter); padding: 40px 80px 0;
        display:grid; grid-template-columns:repeat(auto-fit, minmax(200px, 1fr)); gap: 24px;
        max-width: 1200px; margin: 0 auto;
      }
      .stat-item { text-align:center; padding: 24px; background: var(--bg-light); border-radius: 20px; border: 1px solid rgba(48,60,108,0.05); }
      .stat-num { font-family:'DM Serif Display',Georgia,serif; font-size:48px; color:var(--accent-orange); line-height:1; margin-bottom:12px; }
      .stat-lbl { font-size:14px; font-weight: 500; color:var(--text-secondary); line-height:1.5; }
      @media (max-width:768px) { .stats-strip { padding: 40px 24px 0; } }

      /* ════════════════════════════════════
         ABOUT
      ════════════════════════════════════ */
      .abtus { width:100%; padding:100px 80px; background:var(--bg-lighter); }
      .about-inner { max-width:800px; margin:0 auto; text-align:center; }
      .abth { font-family:'DM Serif Display',Georgia,serif; color:var(--primary-dark); font-size:clamp(32px,4vw,46px); letter-spacing:-0.02em; margin-bottom:16px; }
      .abth-accent { color:var(--accent-orange); font-style: italic; }
      .abtp { color:var(--text-secondary); font-size:18px; line-height:1.8; margin:0 auto; }
      
      .trust-badges { display:flex; gap:24px; justify-content:center; flex-wrap:wrap; margin-top:48px; }
      .trust-badge { display:flex; align-items:center; gap:10px; background:white; box-shadow: var(--shadow-sm); border:1px solid rgba(48,60,108,0.06); border-radius:50px; padding:12px 24px; }
      .trust-badge-icon { width:18px; height:18px; color:var(--primary-color); }
      .trust-badge span { font-size:14px; font-weight:600; color:var(--text-secondary); }
      @media (max-width:768px) { .abtus { padding:80px 24px; } }

      /* ════════════════════════════════════
         DOCTORS
      ════════════════════════════════════ */
      .doctors-wrapper { background: var(--bg-light); padding: 100px 0; position:relative; overflow:hidden; }
      #doc1 { text-align:center; padding:0 80px; max-width: 1400px; margin: 0 auto; }
      #doc1 h2 { font-family:'DM Serif Display',Georgia,serif; font-size:clamp(32px,4vw,46px); color:var(--primary-dark); margin:0 0 12px; }
      #doc1 .featpara { font-size:17px; color:var(--text-light); margin-bottom:0; }
      
      /* ── Card rail ── */
      .rail-outer { position:relative; margin-top:40px; }
      .rail {
        display:flex; gap:24px; text-align:left;
        overflow-x:auto; scroll-snap-type:x mandatory;
        padding:8px 4px 28px; margin:0 -4px;
        scrollbar-width:none; -ms-overflow-style:none;
        scroll-behavior:smooth;
      }
      .rail::-webkit-scrollbar { display:none; }
      .rail:focus-visible { outline:2px solid var(--primary-color); outline-offset:4px; border-radius:24px; }
      .rail > .pro { flex:0 0 340px; width:340px; scroll-snap-align:start; }

      .rail-arrow {
        position:absolute; top:50%; transform:translateY(-50%); z-index:5;
        width:48px; height:48px; border-radius:50%;
        border:1px solid rgba(48,60,108,0.10); background:#fff;
        color:var(--primary-dark); font-size:15px; cursor:pointer;
        display:flex; align-items:center; justify-content:center;
        box-shadow:var(--shadow-md);
        transition:all 0.25s ease;
      }
      .rail-arrow:hover:not(:disabled) {
        background:var(--primary-dark); color:#fff; border-color:var(--primary-dark);
        transform:translateY(-50%) scale(1.06);
      }
      .rail-arrow:disabled { opacity:0; pointer-events:none; }
      .rail-arrow--prev { left:-22px; }
      .rail-arrow--next { right:-22px; }

      /* Fade hints that there is more beyond the edge. */
      .rail-outer::before, .rail-outer::after {
        content:''; position:absolute; top:0; bottom:28px; width:56px;
        pointer-events:none; z-index:2;
      }
      .rail-outer::before { left:-4px; background:linear-gradient(to right, var(--bg-light), transparent); }
      .rail-outer::after  { right:-4px; background:linear-gradient(to left, var(--bg-light), transparent); }
      .pro {
        background:white; border:1px solid rgba(48,60,108,0.06); border-radius:24px;
        padding:32px; display:flex; flex-direction:column; gap:0;
        box-shadow:var(--shadow-md); transition:all 0.3s cubic-bezier(0.4,0,0.2,1);
      }
      .pro:hover { transform:translateY(-6px); box-shadow:var(--shadow-lg); border-color:rgba(48,60,108,0.15); }
      .pro-header { display:flex; align-items:center; gap:16px; margin-bottom:20px; }
      .pro-avatar-img { width:64px; height:64px; border-radius:50%; object-fit:cover; box-shadow:0 4px 12px rgba(0,0,0,0.1); flex-shrink:0; }
      .pro-title { display:flex; flex-direction:column; gap:4px; }
      .docname { font-family:'DM Serif Display',Georgia,serif; font-size:22px; margin:0; color:var(--primary-dark); }
      .doc-stars { display:flex; align-items:center; gap:6px; }
      .star-filled { color:#f5a623; font-size:14px; }
      .doc-rating-num { font-size:14px; color:var(--text-secondary); font-weight:600; margin:0; }
      
      .doc-specialty-row { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:16px; }
      .doc-specialty {
        background: rgba(74,87,157,0.08); color: var(--primary-light); padding:6px 14px; border-radius:50px;
        font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; margin:0;
      }
      .doc-lang-row { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:16px; }
      .doc-lang-pill { font-size:12px; font-weight:600; background:var(--sage-lite); color:var(--accent-sage); padding:4px 12px; border-radius:6px; }
      
      .para { font-size:15px; color:var(--text-secondary); line-height:1.7; margin:0 0 24px; display:-webkit-box; -webkit-line-clamp:4; -webkit-box-orient:vertical; overflow:hidden; }
      .pro-meta { display:flex; justify-content:space-between; align-items:center; padding-top:20px; border-top:1px solid rgba(48,60,108,0.06); margin-top:auto; }
      .pro-exp { font-size:13px; color:var(--text-light); }
      .pro-exp strong { color:var(--primary-dark); font-weight:700; font-size: 14px;}
      
      .pro-book-btn {
        background:var(--primary-dark); color:white; border:none; cursor:pointer; padding:12px 24px; border-radius:50px;
        font-size:14px; font-weight:600; display:flex; align-items:center; gap:8px;
        transition:all 0.3s ease; text-decoration:none; box-shadow: 0 4px 12px rgba(26,35,68,0.2);
      }
      .pro-book-btn:hover { background:var(--primary-color); transform: translateY(-2px); box-shadow: 0 6px 16px rgba(26,35,68,0.3); text-decoration:none; }
      
      .button-container { width:100%; display:flex; justify-content:center; padding:60px 80px 0; }
      .button-28 {
        background:transparent; border:2px solid var(--primary-dark); border-radius:50px; color:var(--primary-dark); cursor:pointer;
        display:inline-flex; align-items:center; justify-content:center; gap:10px;
        font-size:16px; font-weight:600; height:56px; padding:0 36px; text-decoration:none;
        transition:all 0.3s ease;
      }
      .button-28:hover { background:var(--primary-dark); color:#fff; box-shadow:0 8px 24px rgba(26,35,68,0.2); transform:translateY(-2px); }
      
      .button-graphologist {
        background: transparent;
        border: 2px solid var(--accent-orange);
        border-radius: 50px;
        color: var(--accent-orange);
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        font-size: 16px;
        font-weight: 600;
        height: 56px;
        padding: 0 36px;
        text-decoration: none;
        transition: all 0.3s ease;
      }
      .button-graphologist:hover {
        background: var(--accent-orange);
        color: #fff !important;
        box-shadow: 0 8px 24px rgba(232,98,42,0.2);
        transform: translateY(-2px);
      }
      
      @media (max-width:768px) {
        #doc1, #graph1 { padding:0 24px; }
        .button-container { padding: 40px 24px 0; }
        .rail { gap:16px; margin:0 -24px; padding:8px 24px 24px; }
        .rail > .pro { flex:0 0 280px; width:280px; scroll-snap-align:center; }
        .rail-arrow { display:none; }
        .rail-outer::before, .rail-outer::after { display:none; }
      }

      /* ════════════════════════════════════
         TESTIMONIALS
      ════════════════════════════════════ */
      .testimonials-section { background:var(--bg-lighter); padding:100px 80px; max-width: 1200px; margin: 0 auto;}
      .testimonials-header { text-align:center; margin-bottom:64px; }
      .testimonials-header h2 { font-family:'DM Serif Display',Georgia,serif; font-size:clamp(32px,4vw,46px); color:var(--primary-dark); margin:8px 0 16px; letter-spacing:-0.02em; }
      .testimonials-header h2 em { font-style:italic; color:var(--accent-orange); }
      .testimonials-header p { font-size:17px; color:var(--text-light); max-width:600px; margin:0 auto; line-height: 1.7; }
      
      .testimonials-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:32px; }
      .testimonial-card { background:var(--bg-light); border-radius:24px; padding:40px; border:1px solid rgba(48,60,108,0.05); position:relative; transition:all 0.3s ease; box-shadow: var(--shadow-sm); }
      .testimonial-card:hover { transform:translateY(-4px); box-shadow: var(--shadow-md); }
      .testimonial-card.dark { background:var(--primary-dark); border-color:transparent; box-shadow: 0 12px 32px rgba(26,35,68,0.2); }
      
      .testimonial-card.dark .tq { color:white; }
      .testimonial-card.dark .tw { color:rgba(255,255,255,0.7); }
      .testimonial-card.dark .tt { background:rgba(232,98,42,0.2); color:#f4a070; }
      .testimonial-card.dark .tqm { color:rgba(232,98,42,0.3); }
      
      .tqm { font-family:'DM Serif Display',serif; font-size:80px; line-height:0.6; color:rgba(232,98,42,0.15); margin-bottom:16px; display:block; }
      .tq { font-family:'DM Serif Display',Georgia,serif; font-size:22px; font-style:italic; line-height:1.6; color:var(--primary-dark); margin:0 0 24px; }
      .tw { font-size:14px; font-weight: 600; color:var(--text-secondary); margin:0 0 12px; }
      .tt { display:inline-block; font-size:11px; font-weight:700; background:rgba(232,98,42,0.1); color:var(--accent-orange); padding:4px 12px; border-radius:50px; text-transform:uppercase; letter-spacing:0.05em; }
      @media (max-width:900px) { .testimonials-grid { grid-template-columns:1fr; } .testimonials-section { padding:80px 24px; } }

      /* ════════════════════════════════════
         BREATHING
      ════════════════════════════════════ */
      .breathe-section { background:var(--sage-lite); padding:100px 80px; }
      .breathe-inner { max-width:1200px; margin:0 auto; display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:center; }
      .breathe-copy h2 { font-family:'DM Serif Display',Georgia,serif; font-size:clamp(32px,4vw,46px); color:var(--primary-dark); margin:8px 0 20px; }
      .breathe-copy h2 em { font-style:italic; color:var(--accent-orange); }
      .breathe-copy p { color:var(--text-secondary); font-size:17px; line-height:1.8; max-width:500px; }
      .breathe-bullets { list-style:none; margin:32px 0 0; padding:0; display:flex; flex-direction:column; gap:16px; }
      .breathe-bullets li { display:flex; align-items:center; gap:12px; font-size:15px; font-weight: 500; color:var(--text-secondary); }
      .breathe-bullets svg { color:var(--accent-sage); font-size: 18px; }
      
      .breathe-stage { display:flex; flex-direction:column; align-items:center; gap:32px; background: white; padding: 60px 40px; border-radius: 32px; box-shadow: var(--shadow-lg); }
      .breathe-instruction { min-height:40px; text-align:center; }
      .breathe-instruction-text { font-family:'DM Serif Display',Georgia,serif; font-size:28px; transition:color 600ms ease; }
      .breathe-instruction-text.phase-0 { color:var(--accent-orange); }
      .breathe-instruction-text.phase-1 { color:#c4531f; }
      .breathe-instruction-text.phase-2 { color:var(--accent-orange); }
      .breathe-instruction-text.idle { font-family:'DM Sans',sans-serif; color:var(--text-light); font-size:18px; font-weight: 500; }
      
      .breathe-orb {
        position:relative; width:220px; height:220px; border-radius:50%; border:none; cursor:pointer;
        color:#fff; display:flex; align-items:center; justify-content:center; flex-direction:column;
        background:radial-gradient(circle at 32% 32%,#f09060,var(--accent-orange) 60%,#c4531f);
        box-shadow:0 24px 60px rgba(231,106,50,0.30),inset 0 -16px 32px rgba(0,0,0,0.12),inset 0 16px 32px rgba(255,255,255,0.12);
        transition:background 800ms ease,box-shadow 600ms ease;
      }
      .breathe-orb.phase-0 { animation:breatheIn 4s ease-in forwards; }
      .breathe-orb.phase-1 { animation:breatheHold 7s ease forwards; }
      .breathe-orb.phase-2 { animation:breatheOut 8s ease-out forwards; }
      .breathe-orb:not(.active):hover { transform:scale(1.03); }
      @keyframes breatheIn   { from{transform:scale(0.82)} to{transform:scale(1.18)} }
      @keyframes breatheHold { 0%,100%{transform:scale(1.18)} }
      @keyframes breatheOut  { from{transform:scale(1.18)} to{transform:scale(0.82)} }
      
      .breathe-orb-ring, .breathe-orb-ring--2 { position:absolute; border-radius:50%; pointer-events:none; }
      .breathe-orb-ring    { inset:-20px; border:2px solid rgba(232,98,42,0.2); animation:ringPulse 3s ease-in-out infinite; }
      .breathe-orb-ring--2 { inset:-40px; border:1px solid rgba(232,98,42,0.1); animation:ringPulse 3s ease-in-out infinite; animation-delay:0.7s; }
      @keyframes ringPulse { 0%,100%{opacity:0.8;transform:scale(1)} 50%{opacity:0.2;transform:scale(1.05)} }
      
      .breathe-orb-label { position:relative; z-index:1; display:flex; flex-direction:column; align-items:center; gap:4px; }
      .breathe-countdown { font-size:64px; font-family:'DM Serif Display',Georgia,serif; line-height:1; }
      .breathe-countdown-unit { font-size:12px; letter-spacing:0.15em; text-transform:uppercase; opacity:0.8; font-weight: 700; }
      .breathe-orb:not(.active) .breathe-orb-label { font-family:'DM Serif Display',serif; font-size:20px; text-align:center; padding:0 20px; line-height:1.4; }
      
      .breathe-hint { font-size:13px; font-weight: 600; color:var(--text-light); letter-spacing:0.1em; text-transform:uppercase; margin-top: 10px; }
      .breathe-phase-strip { display:flex; gap:10px; flex-wrap:wrap; justify-content:center; }
      .breathe-phase-pill { padding:6px 16px; border-radius:50px; font-size:11px; font-weight:700; letter-spacing:0.05em; text-transform:uppercase; opacity:0.4; transition:all 0.4s ease; }
      .breathe-phase-pill.active { opacity:1; background:rgba(231,106,50,0.12); color:var(--accent-orange); }
      
      @media (max-width:900px) {
        .breathe-section { padding:80px 24px; }
        .breathe-inner { grid-template-columns:1fr; gap:48px; text-align:center; }
        .breathe-copy p { max-width:100%; margin:0 auto; }
        .breathe-bullets li { justify-content:center; }
        .breathe-stage { padding: 40px 20px; }
      }

      /* ════════════════════════════════════
         FAQ
      ════════════════════════════════════ */
      .faq-section { padding:100px 80px; background:var(--bg-lighter); }
      .faq-wrapper { max-width:800px; margin:0 auto; }
      .faq-header { text-align:center; margin-bottom:56px; }
      .faq-header h2 { font-family:'DM Serif Display',Georgia,serif; font-size:clamp(32px,4vw,46px); color:var(--primary-dark); margin:8px 0 0; letter-spacing:-0.02em; }
      
      .faq-list { display:flex; flex-direction:column; gap:16px; }
      .faq-item { background:white; border-radius:20px; border:1px solid rgba(48,60,108,0.08); overflow:hidden; transition:all 0.3s ease; }
      .faq-item.open { border-color:var(--primary-color); box-shadow:var(--shadow-md); }
      .faq-question { width:100%; padding:24px 32px; display:flex; justify-content:space-between; align-items:center; gap:20px; background:transparent; border:none; cursor:pointer; text-align:left; font-family:'DM Serif Display',Georgia,serif; font-size:18px; color:var(--primary-dark); }
      .faq-chevron { color:var(--accent-orange); font-size: 16px; transition:transform 0.3s ease; }
      .faq-item.open .faq-chevron { transform:rotate(180deg); }
      .faq-answer-wrap { max-height:0; overflow:hidden; transition:max-height 0.4s cubic-bezier(0.4,0,0.2,1); }
      .faq-item.open .faq-answer-wrap { max-height:400px; }
      .faq-answer { padding:0 32px 28px; color:var(--text-secondary); font-size:16px; line-height:1.7; margin:0; }
      @media (max-width:768px) { .faq-section { padding:80px 24px; } .faq-question { padding: 20px 24px; font-size: 16px; } .faq-answer { padding: 0 24px 24px; font-size: 15px; } }

      /* ════════════════════════════════════
         FOOTER
      ════════════════════════════════════ */
      footer {
        background:var(--bg-light); color:var(--text-secondary);
        padding:80px 80px 40px; display:grid; grid-template-columns:2fr 1fr 1fr;
        gap:64px; border-top:1px solid rgba(48,60,108,0.06); max-width: 1400px; margin: 0 auto;
      }
      .footer-brand { max-width:360px; }
      .footer-brand-name { font-family:'DM Serif Display',Georgia,serif; font-size:28px; color:var(--primary-dark); margin-bottom:16px; }
      .footer-tagline { font-size:15px; color:var(--text-secondary); line-height:1.7; margin:0 0 32px; }
      
      footer .col { display:flex; flex-direction:column; gap:4px; }
      footer h4 { color:var(--primary-dark); font-size:13px; font-weight:700; letter-spacing:0.1em; text-transform:uppercase; margin-bottom:20px; padding-bottom:12px; border-bottom:1px solid rgba(48,60,108,0.06); }
      footer a { font-size:15px; text-decoration:none; color:var(--text-secondary); margin-bottom:12px; display:block; transition:all 0.2s ease; }
      footer a:hover { color:var(--accent-orange); transform:translateX(4px); }
      
      .footer-social { display:flex; gap:12px; margin-top:8px; }
      .footer-social-icon { width:40px; height:40px; border-radius:50%; background:white; border:1px solid rgba(48,60,108,0.1); display:flex; align-items:center; justify-content:center; color:var(--primary-color); transition:all 0.2s ease; font-size:16px; text-decoration:none; box-shadow: var(--shadow-sm); }
      .footer-social-icon:hover { background:var(--primary-dark); color:white; border-color:var(--primary-dark); transform:translateY(-3px); box-shadow: var(--shadow-md); }
      
      .contact-item { display:flex; align-items:flex-start; gap:12px; margin-bottom:16px; }
      .contact-item-icon { font-size:16px; color:var(--accent-orange); min-width:20px; padding-top:2px; flex-shrink:0; }
      .contact-item-value { font-size:15px; color:var(--text-secondary); line-height:1.5; }
      
      .footer-bottom { grid-column:1 / -1; border-top:1px solid rgba(48,60,108,0.06); padding-top:32px; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:16px; }
      .footer-bottom p { font-size:14px; color:var(--text-light); margin:0; font-weight:500; }
      
      .newsletter-form { margin-top:24px; background: white; padding: 24px; border-radius: 20px; box-shadow: var(--shadow-sm); border: 1px solid rgba(0,0,0,0.04); }
      .newsletter-label { font-size:13px; font-weight: 600; color:var(--primary-dark); margin-bottom:12px; display:block; }
      .newsletter-row { display:flex; flex-direction:column; gap:12px; }
      .newsletter-input { width:100%; padding:14px 16px; border-radius:12px; border:1.5px solid rgba(48,60,108,0.15); background:#f9fafc; font-family:'DM Sans',sans-serif; font-size:14px; outline:none; transition:border-color 0.2s ease; }
      .newsletter-input:focus { border-color:var(--primary-color); background: white; }
      .newsletter-btn { width:100%; padding:14px; border-radius:12px; background:var(--primary-dark); color:#fff; border:none; cursor:pointer; font-family:'DM Sans',sans-serif; font-size:14px; font-weight:700; display:flex; align-items:center; justify-content:center; gap:8px; transition:all 0.3s ease; }
      .newsletter-btn:hover { background:var(--primary-color); transform:translateY(-2px); box-shadow: 0 4px 12px rgba(26,35,68,0.2); }
      
      @media (max-width:900px) { footer { grid-template-columns:1fr; padding:60px 24px 32px; gap:40px; } .footer-brand { max-width:100%; } .footer-bottom { flex-direction:column; text-align:center; } }

      /* ════════════════════════════════════
         FLOATING WHATSAPP & MOBILE CTA
      ════════════════════════════════════ */
      .floating-whatsapp {
        position:fixed; bottom:32px; right:32px; z-index:999;
        width:64px; height:64px; border-radius:50%; background:#25D366; color:#fff;
        display:flex; align-items:center; justify-content:center; font-size:32px;
        text-decoration:none; box-shadow:0 12px 32px rgba(37,211,102,0.4);
        transition:transform 0.3s ease, box-shadow 0.3s ease;
      }
      .floating-whatsapp:hover { transform:translateY(-4px) scale(1.05); box-shadow:0 16px 40px rgba(37,211,102,0.5); color:#fff; }
      
      .sticky-mobile-cta { display:none; position:fixed; bottom:0; left:0; right:0; z-index:998; background:rgba(255,255,255,0.9); backdrop-filter:blur(10px); border-top:1px solid rgba(48,60,108,0.1); padding:16px 24px; box-shadow:0 -8px 32px rgba(26,26,46,0.08); }
      .sticky-mobile-cta-btn { display:flex; align-items:center; justify-content:center; gap:10px; background:var(--primary-dark); color:white; width:100%; height:56px; border-radius:99px; border:none; cursor:pointer; font-size:16px; font-weight:600; box-shadow:0 8px 24px rgba(26,35,68,0.2); }
      @media (max-width:768px) { .sticky-mobile-cta { display:block; } .floating-whatsapp { bottom:100px; right: 24px; width: 56px; height: 56px; font-size: 28px; } }
    `}</style>

    <div>
      {/* ══════════════ HERO ══════════════ */}
      <section id="avatar">
        <div className="hero-bg-img" aria-hidden="true" />
        
        <div className="hero-container">
          <div 
            className="hero-copy" 
            style={{ 
              background: 'rgba(255, 255, 255, 0.88)', 
              padding: '40px', 
              borderRadius: '24px', 
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow: '0 12px 32px rgba(48,60,108,0.08)',
              border: '1px solid rgba(255, 255, 255, 0.6)'
            }}
          >
            <div className="brand-badge">UXAAH</div>
            <div className="hero-eyebrow">
              <span className="hero-eyebrow-dot" style={{width: 8, height: 8, background: '#E76A32', borderRadius: '50%'}} />
              Mental health care for Northeast India
            </div>

            <h1 className="hero-headline">
              You've been carrying<br />
              <span style={{ display: 'block', minHeight: '1.3em' }}>
                <span className="hero-headline-accent" key={phraseIdx}>
                  {ROTATING_PHRASES[phraseIdx]}
                </span>
              </span>
            </h1>

            <p className="hero-subtext">
              Exam stress. Family pressure. That emptiness you can't explain.
              Whatever it is — there's a real person here who speaks your language
              and genuinely wants to listen.
            </p>

            <div className="hero-trust-strip">
              <div className="hero-trust-item">
                <FontAwesomeIcon icon={faUserSecret} className="hero-trust-icon" />
                <span>Anonymous option</span>
              </div>
              <div className="hero-trust-item">
                <FontAwesomeIcon icon={faGlobe} className="hero-trust-icon" />
                <span>8+ languages</span>
              </div>
              <div className="hero-trust-item">
                <FontAwesomeIcon icon={faCertificate} className="hero-trust-icon" />
                <span>Licensed experts</span>
              </div>
            </div>
          </div>

          <div className="hero-interactive-card">
            <h3 className="interactive-title">Find your therapist</h3>
            <p className="interactive-subtitle">Answer one quick question to see who can help.</p>

            <form onSubmit={handleQuickMatch}>
              <div className="form-group">
                <label>What do you need support with today?</label>
                <select
                  className="form-select"
                  value={quickIssue}
                  onChange={(e) => setQuickIssue(e.target.value)}
                  required
                >
                  <option value="" disabled>Select an area...</option>
                  {issueOptions.map(pill => (
                    <option key={pill} value={pill}>{pill}</option>
                  ))}
                </select>
              </div>

              <button type="submit" className="btn-primary-large">
                Match me with a therapist
                <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 14 }} />
              </button>
            </form>
            <Link to="/quiz" className="hero-quiz-link">
              <FontAwesomeIcon icon={faComments} />
              Not sure? Take the 2-minute matching quiz
              <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
            </Link>
            <p className="hero-quiz-note">Matches you by what you're going through and the language you're comfortable in.</p>
          </div>
        </div>
      </section>

      {/* ══════════════ LANGUAGE STRIP ══════════════ */}
      <div className="lang-strip">
        <p className="lang-label-row">Therapy in your language —</p>
        <div className="lang-scroll">
          {[...LANGUAGES, ...LANGUAGES].map((lang, i) => (
            <span key={i} className="lang-item">
              <span className="lang-dot" />
              <strong>{lang}</strong>
            </span>
          ))}
        </div>
      </div>

      {/* ══════════════ LANGUAGE BARRIER ══════════════ */}
      <section id="language" className="langsec">
        <div className="langsec-inner">
          <div className="animate-on-scroll">
            <span className="eyebrow">Breaking the language barrier</span>
            <h2>Say it the way <em>you feel it.</em></h2>
            <p className="langsec-lead">
              Starting therapy is hard enough. It is harder when you have to translate your
              feelings into a language that isn't yours before you can even begin.
              At UXAAH, you choose a therapist who speaks the language you grew up in.
            </p>
            <ul className="langsec-points">
              <li>
                <span className="langsec-point-icon"><FontAwesomeIcon icon={faLanguage} /></span>
                <div>
                  <strong>Pick your therapist by language</strong>
                  <span>Every profile shows the languages they speak, so you know before you book.</span>
                </div>
              </li>
              <li>
                <span className="langsec-point-icon"><FontAwesomeIcon icon={faComments} /></span>
                <div>
                  <strong>Chat or video, in your own words</strong>
                  <span>Describe how you feel, in the words you'd use at home.</span>
                </div>
              </li>
              <li>
                <span className="langsec-point-icon"><FontAwesomeIcon icon={faUserDoctor} /></span>
                <div>
                  <strong>Someone who understands the context</strong>
                  <span>Family expectations, community norms, exam pressure: you don't have to explain them from scratch.</span>
                </div>
              </li>
            </ul>
            <a href="#doc1" className="btn-primary-large" onClick={scrollToTherapists} style={{ display: 'inline-flex', width: 'auto', padding: '14px 32px', borderRadius: 50 }}>
              Find a therapist who speaks my language <FontAwesomeIcon icon={faArrowRight} />
            </a>
          </div>

          <div className="langsec-card animate-on-scroll">
            <p className="langsec-card-title">Our therapists speak</p>
            <div className="langsec-pills">
              {LANGUAGES.map(lang => <span key={lang} className="langsec-pill">{lang}</span>)}
            </div>
            <p className="langsec-note">
              Don't see your language? Tell us on WhatsApp and we'll work on adding a therapist who speaks it.
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════ ANONYMOUS MODE ══════════════ */}
      <section id="anonymous" className="anonsec">
        <div className="anonsec-inner">
          <div className="anonsec-head animate-on-scroll">
            <span className="eyebrow">Safe by design</span>
            <h2>Be seen only as much as <em>you want to be.</em></h2>
            <p>
              Worried about being recognised? Turn on Anonymous mode in a video session.
              Your face is blurred and your voice is altered, so you can open up at your own pace.
            </p>
          </div>

          <div className="anonsec-grid">
            <div className="anonsec-card animate-on-scroll">
              <div className="anonsec-card-icon"><FontAwesomeIcon icon={faVideo} /></div>
              <h3>Blurred video</h3>
              <p>Your camera feed is blurred, so your therapist can talk with you without seeing your face clearly.</p>
            </div>
            <div className="anonsec-card animate-on-scroll" style={{ animationDelay: '80ms' }}>
              <div className="anonsec-card-icon"><FontAwesomeIcon icon={faUserSecret} /></div>
              <h3>Altered voice</h3>
              <p>Your voice is altered to make it harder to recognise, while staying clear enough to talk comfortably.</p>
            </div>
            <div className="anonsec-card animate-on-scroll" style={{ animationDelay: '160ms' }}>
              <div className="anonsec-card-icon"><FontAwesomeIcon icon={faLock} /></div>
              <h3>You stay in control</h3>
              <p>Switch it on or off at any point during the session. Share more of yourself only when you're ready.</p>
            </div>
          </div>

          <p className="anonsec-foot">
            Anonymous mode is available in video sessions. Your therapist is also bound by clinical confidentiality.
          </p>
        </div>
      </section>

      {/* ══════════════ DOES THIS SOUND LIKE YOU ══════════════ */}
      <section id="does-this-sound-like-you" className="problems-section">
        <div className="problems-header animate-on-scroll">
          <span className="eyebrow">Does this sound like you?</span>
          <h2>You don't need a <em>diagnosis</em><br />to deserve support.</h2>
          <p className="sub">
            Tick anything that feels familiar. We'll point you to the kind of
            therapist who works with it. No account, nothing saved.
          </p>
        </div>

        <div className="problems-grid">
          {REAL_PROBLEMS.map((p, i) => (
            <div
              key={i}
              className={`problem-card animate-on-scroll ${checkedProblems.includes(i) ? 'checked' : ''}`}
              style={{ animationDelay: `${i * 60}ms` }}
              onClick={() => toggleProblem(i)}
            >
              <div className="problem-check">{checkedProblems.includes(i) && '✓'}</div>
              <span className="problem-emoji">{p.emoji}</span>
              <p className="problem-text">{p.text}</p>
            </div>
          ))}
        </div>

        <div className="problems-cta-wrap">
          {!showProblemsResult ? (
            <button
              className="btn-primary-large"
              style={{ display: 'inline-flex', width: 'auto', padding: '16px 36px', borderRadius: 50, opacity: checkedProblems.length === 0 ? 0.5 : 1 }}
              onClick={handleProblemsSubmit}
              disabled={checkedProblems.length === 0}
            >
              {checkedProblems.length === 0
                ? 'Select what resonates'
                : `I recognise ${checkedProblems.length} of these. Who can help?`}
              {checkedProblems.length > 0 && <FontAwesomeIcon icon={faArrowRight} />}
            </button>
          ) : (
            <div className="problems-result">
              <p>
                <strong>You just did something that takes real courage.</strong>
                Acknowledging what you're carrying is the first step.
                Our therapists have sat with all of these — many, many times.
                You're not broken. You're just overdue for someone to actually listen.
              </p>

              {problemAreas.length > 0 ? (
                <>
                  <p className="result-lead">Based on what you ticked, therapists in these areas may help:</p>
                  <div className="result-areas">
                    {problemAreas.map(area => (
                      <button key={area} type="button" className="result-area-btn" onClick={() => goToArea(area)}>
                        {area} <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className="result-lead">Not sure what to look for? That's completely fine. The quiz can work it out with you.</p>
              )}

              <Link to="/quiz" className="result-quiz-link">Or take the 2-minute matching quiz →</Link>

              <p className="result-crisis">
                If you are in crisis or thinking about harming yourself, please call Tele-MANAS on 14416 (free, 24×7).
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ══════════════ STATS ══════════════ */}
      <div className="stats-strip">
        {STATS.map((s, i) => (
          <div key={i} className="stat-item animate-on-scroll" style={{ animationDelay: `${i * 80}ms` }}>
            <div className="stat-num">{s.num}</div>
            <div className="stat-lbl">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ══════════════ ABOUT ══════════════ */}
      <section id="aboutus">
        <div className="abtus">
          <div className="about-inner animate-on-scroll">
            <span className="eyebrow">Who We Are</span>
            <h2 className="abth">About <span className="abth-accent">UXAAH</span></h2>
            <p className="abtp">
              UXAAH is Northeast India's first mental health platform that speaks your language — literally.
              We believe the biggest barrier to getting help isn't awareness. It's finding someone who truly
              gets you — your language, your culture, your pressures, your world.
              <br /><br />
              Our team of licensed therapists specialises in addressing a wide range of mental health
              challenges, offering evidence-based treatments tailored to individual needs — in a safe,
              non-judgmental environment.
            </p>
            <div className="trust-badges">
              {[
                { icon: faShieldHalved, label: '100% Confidential' },
                { icon: faCertificate,  label: 'Licensed Therapists' },
                { icon: faGlobe,        label: '8+ Languages' },
                { icon: faUserSecret,   label: 'Anonymous Option' },
                { icon: faUserDoctor,   label: 'Verified Professionals' },
              ].map((b, i) => (
                <div className="trust-badge" key={i}>
                  <FontAwesomeIcon icon={b.icon} className="trust-badge-icon" />
                  <span>{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════ DOCTORS & GRAPHOLOGISTS ══════════════ */}
      <div className="doctors-wrapper">
        <section id="doc1">
          <span className="eyebrow animate-on-scroll">Our Team</span>
          <h2 className="animate-on-scroll">Meet Our Expert Therapists</h2>
          <p className="featpara animate-on-scroll">
            Real humans. Warm, trained, and from communities like yours.
          </p>

          <CardRail label="therapists">
            {displayDoctors.map((doc, i) => (
              <div className="pro animate-on-scroll" key={doc.id || doc.name} style={{ animationDelay: `${i * 80}ms` }}>
                <div className="pro-header">
                  <img
                    src={doc.image ? `${BACKEND_URL}/uploads/${doc.image}` : '/img/6971412.png'}
                    alt={doc.name}
                    className="pro-avatar-img"
                    onError={(e) => { e.target.src = '/img/6971412.png'; }}
                  />
                  <div className="pro-title">
                    <h5 className="docname">{doc.name}</h5>
                    <StarRating rating={doc.rating} />
                  </div>
                </div>

                <div className="doc-specialty-row">
                  {Array.isArray(doc.spec)
                    ? doc.spec.map((s, idx) => <span key={idx} className="doc-specialty">{s}</span>)
                    : <span className="doc-specialty">{doc.spec}</span>
                  }
                </div>

                {doc.languages && doc.languages.length > 0 && (
                  <div className="doc-lang-row">
                    {doc.languages.map(lang => (
                      <span key={lang} className="doc-lang-pill">{lang}</span>
                    ))}
                  </div>
                )}

                <p className="para">"{doc.bio}"</p>

                <div className="pro-meta">
                  <div className="pro-exp">
                    {doc.experience && <><strong>{doc.experience}</strong>{doc.sessions ? ' · ' : ''}</>}
                    {doc.sessions && `${doc.sessions} sessions`}
                  </div>
                  {/* Books through the platform rather than a WhatsApp link to a
                      single number — the booking flow is what records the
                      appointment, sends reminders, and enables the video call. */}
                  <Link
                    to={doc.id ? `/bookings/${doc.id}` : '/doctors?tab=therapists'}
                    className="pro-book-btn"
                  >
                    Book <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
                  </Link>
                </div>
              </div>
            ))}
          </CardRail>
        </section>

        <div className="button-container animate-on-scroll">
          <Link to="/doctors?tab=therapists" className="button-28">
            View All Therapists <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 13 }} />
          </Link>
        </div>

        {/* ══════════════ GRAPHOLOGISTS ══════════════ */}
        {dbGraphologists.length > 0 && (
          <>
            <section id="graph1" style={{ textAlign: 'center', padding: '100px 80px 0', maxWidth: '1400px', margin: '0 auto' }}>
              <span className="eyebrow animate-on-scroll">Handwriting Analysis</span>
              <h2 className="animate-on-scroll">Discover Yourself Through Ink</h2>
              <p className="featpara animate-on-scroll" style={{ marginBottom: '40px' }}>
                Upload a sample of your handwriting and let our experts reveal your hidden strengths.
              </p>

              <CardRail label="graphologists">
                {dbGraphologists.map((g, i) => (
                  <div className="pro animate-on-scroll" key={g.id} style={{ animationDelay: `${i * 80}ms` }}>
                    <div className="pro-header">
                      <img
                        src={g.image ? `${BACKEND_URL}/uploads/${g.image}` : '/img/6971412.png'}
                        alt={g.name}
                        className="pro-avatar-img"
                        onError={(e) => { e.target.src = '/img/6971412.png'; }}
                      />
                      <div className="pro-title">
                        <h5 className="docname">{g.name}</h5>
                      </div>
                    </div>

                    <div className="doc-specialty-row">
                      <span className="doc-specialty">{g.spec}</span>
                    </div>

                    <p className="para">"{g.bio}"</p>

                    <div className="pro-meta">
                      <div className="pro-exp">
                        {g.experience && <><strong>{g.experience}</strong>{g.sessions ? ' · ' : ''}</>}
                        {g.sessions && `${g.sessions} analyses`}
                      </div>
                      <Link
                        to={`/booking/graphologist/${g.id}`}
                        className="pro-book-btn"
                        style={{ background: 'var(--accent-orange)' }}
                      >
                        Analyze <FontAwesomeIcon icon={faPenNib} />
                      </Link>
                    </div>
                  </div>
                ))}
              </CardRail>
            </section>

            <div className="button-container animate-on-scroll" style={{ paddingTop: '40px' }}>
              <Link to="/doctors?tab=graphologists" className="button-graphologist">
                View All Graphologists <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 13 }} />
              </Link>
            </div>
          </>
        )}
      </div>

      {/* ══════════════ TESTIMONIALS ══════════════ */}
      <section className="testimonials-section">
        <div className="testimonials-header animate-on-scroll">
          <span className="eyebrow">Real stories</span>
          <h2>You are <em>not</em> the only one<br />who felt this way.</h2>
          <p>Real words from real people in Northeast India. Names withheld, feelings intact.</p>
        </div>
        <div className="testimonials-grid">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className={`testimonial-card animate-on-scroll ${i === 0 ? 'dark' : ''}`} style={{ animationDelay: `${i * 80}ms` }}>
              <span className="tqm">"</span>
              <p className="tq">{t.quote}</p>
              <p className="tw">— {t.who}</p>
              <span className="tt">{t.tag}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════ BREATHING ══════════════ */}
      <section id="breathe" className="breathe-section">
        <div className="breathe-inner">
          <div className="breathe-copy animate-on-scroll">
            <span className="eyebrow">A small gift from us</span>
            <h2>Feeling overwhelmed <em>right now?</em></h2>
            <p>
              Take 19 seconds. This is the <strong>4-7-8 breathing technique</strong> —
              backed by research, used in therapy rooms worldwide to calm an anxious nervous system.
              No signup. No tracking. Just breath.
            </p>
            <ul className="breathe-bullets">
              <li><FontAwesomeIcon icon={faCheckCircle} /> Breathe in through your nose — 4 seconds</li>
              <li><FontAwesomeIcon icon={faCheckCircle} /> Hold your breath — 7 seconds</li>
              <li><FontAwesomeIcon icon={faCheckCircle} /> Exhale slowly through your mouth — 8 seconds</li>
            </ul>
          </div>

          <div className="breathe-stage animate-on-scroll" style={{ minHeight: '480px', justifyContent: 'flex-start', paddingTop: '40px' }}>
            
            <div className="breathe-instruction" style={{ height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {breathing ? (
                <span className={`breathe-instruction-text phase-${breathPhaseIdx}`}>
                  {currentPhase.label}
                </span>
              ) : (
                <span className="breathe-instruction-text idle">Tap the orb to begin</span>
              )}
            </div>

            <div className="breathe-orb-wrapper" style={{ width: '260px', height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, margin: '20px 0' }}>
              <button
                className={`breathe-orb ${breathing ? `active phase-${breathPhaseIdx}` : ''}`}
                onClick={toggleBreathing}
                aria-label={breathing ? 'Stop breathing exercise' : 'Start breathing exercise'}
              >
                {breathing && (
                  <>
                    <span className="breathe-orb-ring" aria-hidden="true" />
                    <span className="breathe-orb-ring breathe-orb-ring--2" aria-hidden="true" />
                  </>
                )}
                <span className="breathe-orb-label">
                  {breathing ? (
                    <>
                      <span className="breathe-countdown">{countdown}</span>
                      <span className="breathe-countdown-unit">sec</span>
                    </>
                  ) : (
                    'Start'
                  )}
                </span>
              </button>
            </div>

            <p className="breathe-hint" style={{ height: '20px', margin: 0, display: 'flex', alignItems: 'center' }}>
              {breathing ? 'tap to stop' : '\u00A0'}
            </p>

            <div 
              className="breathe-phase-strip" 
              style={{ 
                height: '32px', 
                opacity: breathing ? 1 : 0, 
                visibility: breathing ? 'visible' : 'hidden',
                transition: 'opacity 0.4s ease',
                display: 'flex',
                alignItems: 'center',
                marginTop: '10px'
              }}
            >
              {BREATH_PHASES.map((p, i) => (
                <span key={p.label} className={`breathe-phase-pill ${i === breathPhaseIdx ? 'active' : ''}`}>
                  {p.label} · {p.seconds}s
                </span>
              ))}
            </div>

          </div>

        </div>
      </section>

      {/* ══════════════ FAQ ══════════════ */}
      <section className="faq-section">
        <div className="faq-wrapper">
          <div className="faq-header animate-on-scroll">
            <span className="eyebrow">FAQ</span>
            <h2>Things people often ask</h2>
          </div>
          <div className="faq-list">
            {FAQS.map((faq, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className={`faq-item ${open ? 'open' : ''}`}>
                  <button onClick={() => setOpenFaq(open ? -1 : i)} className="faq-question">
                    <span>{faq.q}</span>
                    <FontAwesomeIcon icon={faChevronDown} className="faq-chevron" />
                  </button>
                  <div className="faq-answer-wrap">
                    <p className="faq-answer">{faq.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════ FOOTER ══════════════ */}
      <footer id="contactus">
        <div className="footer-brand">
          <div className="footer-brand-name">UXAAH</div>
          <p className="footer-tagline">
            Northeast India's mental health platform. Therapy in your language, at your pace, from wherever you are.
          </p>
          <form onSubmit={handleNewsletter} className="newsletter-form">
            <span className="newsletter-label">Get gentle weekly tips for your mind</span>
            <div className="newsletter-row">
              <input
                type="email" placeholder="you@email.com"
                value={newsletterEmail}
                onChange={e => setNewsletterEmail(e.target.value)}
                className="newsletter-input"
              />
              <button type="submit" className="newsletter-btn">
                <FontAwesomeIcon icon={faPaperPlane} />
                Send me tips
              </button>
            </div>
            {newsletterStatus && <p style={{ color: 'var(--accent-orange)', fontSize: 13, marginTop: 12, fontWeight: 600 }}>{newsletterStatus}</p>}
          </form>
          <div className="footer-social" style={{ marginTop: 32 }}>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="Facebook"><FontAwesomeIcon icon={faFacebookF} /></a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="Instagram"><FontAwesomeIcon icon={faInstagram} /></a>
            <a href="https://wa.me/919085647527" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="WhatsApp"><FontAwesomeIcon icon={faWhatsapp} /></a>
          </div>
        </div>

        <div className="col">
          <h4>Contact Us</h4>
          <div className="contact-item">
            <FontAwesomeIcon icon={faLocationDot} className="contact-item-icon" />
            <span className="contact-item-value">Assam — 784001</span>
          </div>
          <div className="contact-item">
            <FontAwesomeIcon icon={faPhone} className="contact-item-icon" />
            <span className="contact-item-value">+91 90856 47527</span>
          </div>
          <div className="contact-item">
            <FontAwesomeIcon icon={faEnvelope} className="contact-item-icon" />
            <span className="contact-item-value">uxah@gmail.com</span>
          </div>
          <div className="contact-item">
            <FontAwesomeIcon icon={faClock} className="contact-item-icon" />
            <span className="contact-item-value">10 AM – 7 PM, Mon–Sat</span>
          </div>
        </div>

        <div className="col">
          <h4>Company</h4>
          <Link to="/#aboutus">About Us</Link>
          <Link to="/doctors">Our Therapists</Link>
          <Link to="/services">Services</Link>
          <Link to="/plans">Pricing Plans</Link>
        </div>

        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} UXAAH. All rights reserved.</p>
          <p>Made with care in Assam, India 🌿</p>
        </div>
      </footer>

    </div>

    {/* ══════════════ FLOATING WHATSAPP ══════════════ */}
    <a
      href="https://wa.me/919085647527?text=Hi%20UXAAH%2C%20I%27d%20like%20to%20know%20more"
      target="_blank" rel="noopener noreferrer"
      className="floating-whatsapp"
      aria-label="Chat on WhatsApp"
    >
      <FontAwesomeIcon icon={faWhatsapp} />
    </a>

    {/* ══════════════ STICKY MOBILE CTA ══════════════ */}
    <div className="sticky-mobile-cta">
      <button className="sticky-mobile-cta-btn" onClick={scrollToTherapists}>
        Talk to someone today <FontAwesomeIcon icon={faArrowRight} />
      </button>
    </div>
    </>
  );
};

export default HomePage;